import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useBookingDraft — Phase 1, P1
 * ─────────────────────────────
 * Auto-saves the booking form state to localStorage (debounced) so customers
 * never lose what they typed if they refresh, switch tabs, or accidentally
 * close the booking dialog.
 *
 * Why this is a separate hook (and not inlined into Orders.jsx):
 *   - Orders.jsx is already ~2000 LOC; this isolates the localStorage
 *     surface so it can be unit-tested independently.
 *   - The draft key is user-scoped (per user_id) so two accounts logging
 *     in on the same browser never see each other's drafts.
 *
 * Storage contract (localStorage):
 *   key   : `pickngo_booking_draft_v1::<user_id>`
 *   value : JSON.stringify({ form: <form fields>, saved_at: <ISO string> })
 *
 * Excluded from persistence:
 *   - File / image / lat-lng state (the dialog re-derives these from
 *     `pickup_address` / `delivery_address` text when the user reopens).
 *   - Any field whose persisted value is empty AND its default value is
 *     also empty (keeps the blob small).
 *
 * Public API:
 *   const {
 *     draft,             // null OR { form, saved_at }
 *     scheduleSave,      // (formObj) => void  — debounced 600 ms
 *     clearDraft,        // () => void          — call on successful order
 *     dismissDraft,      // () => void          — call when user clicks Discard
 *   } = useBookingDraft(userId);
 */
const STORAGE_PREFIX = "pickngo_booking_draft_v1::";
const DEBOUNCE_MS = 600;
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function storageKey(userId) {
  return userId ? `${STORAGE_PREFIX}${userId}` : null;
}

function readDraft(userId) {
  const key = storageKey(userId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.form || typeof parsed.form !== "object") return null;

    // Expire stale drafts so users don't see a "Resume" banner referring
    // to something they typed a month ago.
    const savedAt = parsed.saved_at ? new Date(parsed.saved_at).getTime() : 0;
    if (savedAt && Date.now() - savedAt > MAX_DRAFT_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (err) {
    // Corrupted JSON — drop it silently and start fresh.
    console.warn("useBookingDraft: failed to parse stored draft", err);
    return null;
  }
}

function hasMeaningfulInput(form) {
  if (!form || typeof form !== "object") return false;
  // We consider the draft "worth restoring" only if at least one of the
  // major fields a customer types has content. Otherwise the banner would
  // pop up on every empty open.
  const meaningfulKeys = [
    "pickup_address",
    "delivery_address",
    "package_description",
    "package_weight",
    "coupon_code",
    "notes",
    "pickup_contact_name",
    "pickup_phone",
    "delivery_contact_name",
    "delivery_phone",
  ];
  return meaningfulKeys.some((k) => {
    const v = form[k];
    return typeof v === "string" && v.trim().length > 0;
  });
}

export default function useBookingDraft(userId) {
  const [draft, setDraft] = useState(() => readDraft(userId));
  const timerRef = useRef(null);

  // When user changes (login / logout / account switch), re-read.
  useEffect(() => {
    setDraft(readDraft(userId));
  }, [userId]);

  // Flush any pending debounced write when the component unmounts.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const scheduleSave = useCallback(
    (formObj) => {
      const key = storageKey(userId);
      if (!key) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          if (!hasMeaningfulInput(formObj)) {
            // Auto-save with an empty form is a NO-OP — not a destructive
            // wipe. The only legitimate paths that delete the draft are
            // user-initiated: Discard button, clearDraft on order-placed.
            // Without this guard, the form-reset that runs on dialog
            // close + reopen would auto-erase the saved draft right when
            // the user comes back to restore it.
            return;
          }
          const payload = {
            form: formObj,
            saved_at: new Date().toISOString(),
          };
          localStorage.setItem(key, JSON.stringify(payload));
          // Mirror the just-saved snapshot into React state — without
          // this, same-session close→reopen reads the stale mount-time
          // value (null on a fresh session) and the banner never shows.
          setDraft(payload);
        } catch (err) {
          // Quota full / disabled — non-fatal, draft just won't persist.
          console.warn("useBookingDraft: save failed", err);
        }
      }, DEBOUNCE_MS);
    },
    [userId]
  );

  const clearDraft = useCallback(() => {
    const key = storageKey(userId);
    if (!key) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      localStorage.removeItem(key);
    } catch (err) {
      console.warn("useBookingDraft: clear failed", err);
    }
    setDraft(null);
  }, [userId]);

  // dismissDraft is identical to clearDraft today — kept as a distinct
  // export so call sites read self-documenting (Discard vs. order placed).
  const dismissDraft = clearDraft;

  // Re-read localStorage on demand. Useful when the consumer wants to
  // refresh the in-memory draft without unmounting (e.g. when the
  // booking dialog is opened, to pick up writes from another tab or
  // earlier in the same session). Returns the freshly-read value so
  // callers can act synchronously without waiting for the re-render.
  const refresh = useCallback(() => {
    const next = readDraft(userId);
    setDraft(next);
    return next;
  }, [userId]);

  return { draft, scheduleSave, clearDraft, dismissDraft, refresh };
}
